import {
  AnchorButton,
  Callout,
  Classes,
  Collapse,
  IconSize,
  Intent,
  Spinner,
} from '@blueprintjs/core';
import classNames from 'classnames';
import * as React from 'react';
import { Dialog, Tooltip } from 'vet-bones/components';
import { isEqual } from 'vet-bones/utils';

import { TEST_IDS } from 'src/constants/testIds';
import { useCommitOneResourceOneClassCallback } from 'src/containers/ProjectResourceSuggestionsDialog/hooks/useCommitOneResourceOneClassCallback';
import { useCreateMappings } from 'src/containers/ProjectResourceSuggestionsDialog/hooks/useCreateMappings';
import { useFetchMappingSuggestions } from 'src/containers/ProjectResourceSuggestionsDialog/hooks/useFetchMappingSuggestions';
import { ProjectResourceSelectionPanel } from 'src/containers/ProjectResourceSuggestionsDialog/ProjectResourceSelectionPanel';
import {
  ProjectResourceSuggestionsData,
  ProjectResourceSuggestionsDataContext,
} from 'src/containers/ProjectResourceSuggestionsDialog/projectResourceSuggestionsData';
import { IgnoredSuggestion } from 'src/containers/ProjectResourceSuggestionsDialog/utils/getOrganizedSuggestionRecords';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { useDidValueChange } from 'src/utils/hooks/useDidValueChange';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';

export const ProjectResourceSuggestionsDialog: React.VFC = () => {
  const dispatch = useReduxDispatch();
  const {
    isOpen,
    initialClassIris = [],
    initialIgnoredSuggestions = null,
    initialMappingSuggestions = null,
    initialResourceIds = [],
  } = useReduxSelector((state) => state.ui.resourceSuggestionsDialog);

  const [selectedClasses, setSelectedClasses] =
    React.useState<string[]>(initialClassIris);
  const [selectedResources, setSelectedResources] =
    React.useState<string[]>(initialResourceIds);

  const [ignoredSuggestions, setIgnoredSuggestions] = React.useState<
    IgnoredSuggestion[] | null
  >(initialIgnoredSuggestions);

  const commitOneResourceOneClass = useCommitOneResourceOneClassCallback();
  const { fetchMappingSuggestions, isLoading } = useFetchMappingSuggestions();

  const handleCloseDialog = React.useCallback(
    () =>
      dispatch(
        uiSlice.actions.toggledResourceSuggestionsDialog({
          isOpen: false,
        })
      ),
    [dispatch]
  );

  // reset initial values when dialog opens
  const isOpenChanged = useDidValueChange(isOpen);
  React.useEffect(() => {
    if (isOpenChanged && isOpen) {
      setIgnoredSuggestions(initialIgnoredSuggestions);
      setSelectedClasses(initialClassIris);
      setSelectedResources(initialResourceIds);
    }
  }, [
    initialClassIris,
    initialIgnoredSuggestions,
    initialMappingSuggestions,
    initialResourceIds,
    isOpen,
    isOpenChanged,
  ]);

  const selectedClassesChanged = useDidValueChange(selectedClasses);
  const selectedResourcesChanged = useDidValueChange(selectedResources);
  React.useEffect(() => {
    if (
      (selectedClassesChanged || selectedResourcesChanged) &&
      ignoredSuggestions &&
      (!initialIgnoredSuggestions ||
        !isEqual(selectedClasses, initialClassIris) ||
        !isEqual(selectedResources, initialResourceIds))
    ) {
      setIgnoredSuggestions(null);
    }
  }, [
    ignoredSuggestions,
    initialClassIris,
    initialIgnoredSuggestions,
    initialResourceIds,
    selectedClasses,
    selectedClassesChanged,
    selectedResources,
    selectedResourcesChanged,
  ]);

  const canProceed = !isLoading && selectedResources.length > 0;

  const handleCreateMappings = useCreateMappings();

  const handleNextClick = React.useCallback(async () => {
    if (isLoading) {
      return;
    }

    // Handle one-to-one mapping
    if (selectedResources.length === 1 && selectedClasses.length === 1) {
      commitOneResourceOneClass({ selectedClasses, selectedResources });
    } else {
      const loaded = await fetchMappingSuggestions({
        selectedClasses,
        selectedResources,
      });
      if (loaded.mappingSuggestions?.length) {
        handleCreateMappings(loaded.mappingSuggestions);
      } else {
        setIgnoredSuggestions(loaded.ignoredSuggestions);
      }
    }
  }, [
    commitOneResourceOneClass,
    fetchMappingSuggestions,
    handleCreateMappings,
    isLoading,
    selectedClasses,
    selectedResources,
  ]);

  const data: ProjectResourceSuggestionsData = {
    selectedClasses,
    selectedResources,
    setSelectedClasses,
    setSelectedResources,
  };

  return (
    <ProjectResourceSuggestionsDataContext.Provider value={data}>
      <Dialog
        autoFocus={false}
        canEscapeKeyClose={false}
        canOutsideClickClose={false}
        className={classNames(
          'sd-dialog-project-resource-suggestions',
          'sd-dialog-prompt'
        )}
        closeIconTestId={TEST_IDS.dialog.closeIcon}
        enforceFocus={false}
        isOpen={isOpen}
        lazy={false}
        onClose={handleCloseDialog}
        title={copy.components.resourceSuggestionsDialog.selection.title}
      >
        <div
          className={Classes.DIALOG_BODY}
          data-testid={TEST_IDS.resourceSuggestionsDialog.container}
        >
          <ProjectResourceSelectionPanel />
          <Collapse isOpen={Boolean(ignoredSuggestions)}>
            <Callout intent={Intent.WARNING}>
              {copy.components.resourceSuggestionsDialog.selection.noSuggestions(
                Boolean(ignoredSuggestions?.length)
              )}
            </Callout>
          </Collapse>
        </div>
        <div className={Classes.DIALOG_FOOTER}>
          <div className={Classes.DIALOG_FOOTER_ACTIONS}>
            <AnchorButton
              data-testid={TEST_IDS.resourceSuggestionsDialog.cancelButton}
              intent={Intent.PRIMARY}
              onClick={handleCloseDialog}
              outlined
            >
              {copy.components.resourceSuggestionsDialog.footer.cancel}
            </AnchorButton>
            <Tooltip
              content={
                copy.components.resourceSuggestionsDialog.selection.invalid
              }
              disabled={canProceed}
            >
              <AnchorButton
                data-testid={TEST_IDS.resourceSuggestionsDialog.nextButton}
                disabled={!canProceed}
                icon={
                  isLoading ? <Spinner size={IconSize.STANDARD} /> : undefined
                }
                intent={Intent.PRIMARY}
                onClick={handleNextClick}
              >
                {copy.components.resourceSuggestionsDialog.footer.create}
              </AnchorButton>
            </Tooltip>
          </div>
        </div>
      </Dialog>
    </ProjectResourceSuggestionsDataContext.Provider>
  );
};
